// Orchestrates the permanent vs. per-loop split described in
// GAME_CREATION_INSTRUCTIONS.md: death resets LoopState but never
// PermanentState, and the player cannot manually restart the loop.

import type {
  AbilityId,
  AbilityLevel,
  ArmyStack,
  FactionId,
  FactionTrustLevel,
  GameState,
  LocationId,
  LoopState,
  PermanentState,
  UnitId,
} from "../types"
import { createInitialAntiPlayerState } from "../types"
import { advanceDays } from "./timeEngine"
import { addWorldFlag, removeWorldFlag, resolveEventsForRange } from "./eventEngine"
import { travel as travelToRoute } from "./travelEngine"
import { canTrain, trainAbility as applyAbilityTraining } from "./abilityEngine"
import { discoverLocationAndNeighbors, discoverRoute } from "./mapEngine"
import { getTrust, setTrust, TRUST_ORDER } from "./factionEngine"
import {
  addUnitsAtLocation,
  leaveUnits,
  releaseUnits,
  takeAllUnits,
  takeUnits,
} from "./armyEngine"
import { getAbilityById, getEventById } from "./content"
import { getNextArmorTier } from "./armorEngine"
import { observeAlly, observeRoute, observeTrainingSite } from "./antiPlayerEngine"
import {
  accusationClearedFlag,
  accusationFlag,
  isFramedUncleared,
  pickAccusedFactionId,
} from "./accusationEngine"

export const STARTING_LOCATION_ID = "dawnmere"

// The Day 512 deadline stays hidden from the player until they first
// survive through Day 30, at which point it is permanently revealed.
const DEADLINE_REVEAL_DAY = 30

/**
 * The Necromancer's ritual deadline for the *first* loop, per
 * GAME_CREATION_INSTRUCTIONS.md's 512-day campaign. Later loops get less time —
 * see getRitualDeadlineDay — so this is the ceiling, not a fixed date.
 */
export const RITUAL_DEADLINE_DAY = 512

/**
 * How many days earlier the ritual completes with each loop that passes. The
 * rival is not idle across the deaths the player spends "farming" fresh 512-day
 * windows: every loop left unfinished lets his work gain a week on the world.
 * This is the cost the loop mechanic otherwise lacks — churn enough lifetimes
 * and the winning window closes on you.
 */
export const DEADLINE_ADVANCE_PER_LOOP = 7

/**
 * The earliest the deadline is ever allowed to fall. A relentless optimiser can
 * still be squeezed by the shrinking clock, but never into a mathematically
 * unwinnable save — an efficient winning loop always fits inside this floor.
 */
export const MIN_RITUAL_DEADLINE_DAY = 200

/** The day the ritual completes for a given loop: 512 on the first, a week earlier each loop after, floored. */
export function getRitualDeadlineDay(loopNumber: number): number {
  return Math.max(
    MIN_RITUAL_DEADLINE_DAY,
    RITUAL_DEADLINE_DAY - DEADLINE_ADVANCE_PER_LOOP * (loopNumber - 1),
  )
}

/**
 * How much larger every army in the game is than its bare tabletop numbers —
 * the single knob behind the "epic scale" of the battles. Command Points (and
 * so the size of the army the player can lead), every contingent a faction
 * lends, and every stack a battle fields (see Battle.scale) are all multiplied
 * up from small authored numbers, so a fight looks like the war it is rather
 * than a skirmish of a dozen soldiers. Because damage and health both scale
 * with a stack's count, multiplying both sides together leaves the balance
 * untouched while making the numbers read as hundreds and thousands.
 */
export const ARMY_SCALE = 15

/**
 * Leadership unlocks Command Points, not just numbers on their own — see
 * GAME_CREATION_INSTRUCTIONS.md's Phase 3 ability-unlock examples ("Advanced:
 * earn additional Command Points"). Command Points themselves reset every
 * loop like the rest of LoopState, but the bonus a trained leader starts
 * each loop with is permanent.
 */
function getLeadershipCommandPointBonus(level: AbilityLevel): number {
  const base = (() => {
    switch (level) {
      case "beginner":
        return 3
      case "advanced":
        return 8
      case "master":
        return 15
      default:
        return 0
    }
  })()
  return base * ARMY_SCALE
}

export function getStartingCommandPoints(permanent: PermanentState): number {
  return getLeadershipCommandPointBonus(permanent.abilities["leadership"] ?? "none")
}

export function createInitialPermanentState(): PermanentState {
  return discoverLocationAndNeighbors(
    {
      avatarIndex: 0,
      abilities: {},
      knowledge: [],
      discoveredLocations: [],
      discoveredRoutes: [],
      spiritProgress: 0,
      antiPlayerState: createInitialAntiPlayerState(),
      totalDeaths: 0,
      longestSurvival: 0,
      deadlineRevealed: false,
      spiritScenesSeen: [],
      spiritQuestionsAsked: [],
      armorTier: 0,
    },
    STARTING_LOCATION_ID,
  )
}

export function createNewLoop(loopNumber: number, permanent: PermanentState): LoopState {
  // The one thing that carries a mark *into* the fresh loop rather than out of
  // it: the realm the rival has forged the player out of this time. He decides
  // it from everything he has watched (permanent), so the flag can be laid down
  // the moment the loop is born, before the player has done anything to earn it.
  const accusedFactionId = pickAccusedFactionId(permanent, loopNumber)
  return {
    loopNumber,
    currentDay: 1,
    currentLocationId: STARTING_LOCATION_ID,
    alive: true,
    money: 0,
    factionTrust: {},
    commandPoints: getStartingCommandPoints(permanent),
    armiesByLocation: {},
    currentArmy: [],
    worldFlags: accusedFactionId ? [accusationFlag(accusedFactionId)] : [],
    completedEvents: [],
    defeatedEnemyGroups: [],
    subjectedFactions: [],
  }
}

export function createNewGame(): GameState {
  const permanent = createInitialPermanentState()
  return {
    permanent,
    loop: createNewLoop(1, permanent),
  }
}

function revealDeadlineIfSurvived(
  permanent: PermanentState,
  loop: LoopState,
): PermanentState {
  if (permanent.deadlineRevealed) return permanent
  if (!loop.alive || loop.currentDay <= DEADLINE_REVEAL_DAY) return permanent
  return { ...permanent, deadlineRevealed: true }
}

/** Merges new knowledge ids into permanent state, ignoring ones already known. */
function addKnowledge(
  permanent: PermanentState,
  knowledgeIds: string[],
): PermanentState {
  const newKnowledge = knowledgeIds.filter(
    (id) => !permanent.knowledge.includes(id),
  )
  if (newKnowledge.length === 0) return permanent
  return {
    ...permanent,
    knowledge: [...permanent.knowledge, ...newKnowledge],
  }
}

/**
 * Grants permanent knowledge for any event that newly completed between
 * prevLoop and nextLoop. Applies regardless of whether the player witnessed
 * the event directly (e.g. news of an attack reaches you even if you fled).
 */
function applyNewlyCompletedEventKnowledge(
  permanent: PermanentState,
  prevLoop: LoopState,
  nextLoop: LoopState,
): PermanentState {
  const newlyCompleted = nextLoop.completedEvents.filter(
    (id) => !prevLoop.completedEvents.includes(id),
  )
  const newKnowledge = newlyCompleted.flatMap(
    (id) => getEventById(id)?.grantsKnowledge ?? [],
  )
  return addKnowledge(permanent, newKnowledge)
}

/** Advances the calendar within the current loop, resolving any events that occur along the way. */
export function advanceDay(state: GameState, days = 1): GameState {
  if (!state.loop.alive) return state
  const dayBeforeAdvance = state.loop.currentDay
  const advanced = advanceDays(state.loop, days)
  const withEvents = resolveEventsForRange(
    advanced,
    dayBeforeAdvance + 1,
    advanced.currentDay,
  )
  const permanent = applyNewlyCompletedEventKnowledge(
    revealDeadlineIfSurvived(state.permanent, withEvents),
    state.loop,
    withEvents,
  )
  return { loop: withEvents, permanent }
}

/** Travels along a route, advancing the calendar by its travel duration. */
export function travel(state: GameState, routeId: string): GameState {
  if (!state.loop.alive) return state
  const loop = travelToRoute(state.loop, routeId, state.permanent)
  const discovered = discoverLocationAndNeighbors(
    discoverRoute(state.permanent, routeId),
    loop.currentLocationId,
  )
  const permanent = applyNewlyCompletedEventKnowledge(
    // The anti-player is watching this road. Recording it here rather than at
    // the UI layer means every journey feeds his ambush planning, however the
    // player triggered it.
    observeRoute(revealDeadlineIfSurvived(discovered, loop), routeId),
    state.loop,
    loop,
  )
  return { loop, permanent }
}

/**
 * Records a player decision as a world flag. Base events check these flags
 * (via their preventedByFlags list) so a decision can prevent or change an
 * otherwise-deterministic event.
 */
export function setWorldFlag(state: GameState, flag: string): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: addWorldFlag(state.loop, flag) }
}

/** Lifts a world flag — used for the few flags that describe reversible state, such as a stronghold changing hands. */
export function clearWorldFlag(state: GameState, flag: string): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: removeWorldFlag(state.loop, flag) }
}

/** Loot from a won battle. Money is loop-scoped, like Command Points — it resets with everything else on death. */
export function addMoney(state: GameState, amount: number): GameState {
  if (amount === 0) return state
  return { ...state, loop: { ...state.loop, money: state.loop.money + amount } }
}

/**
 * Buys the next suit of armour with this loop's coin. Spends loop-scoped money
 * but records the suit in *permanent* state, so the purchase outlives the death
 * that clears the purse — see armorEngine. A no-op when the player already wears
 * the best suit or cannot afford the next.
 */
export function buyArmor(state: GameState): GameState {
  if (!state.loop.alive) return state
  const next = getNextArmorTier(state.permanent)
  if (!next || state.loop.money < next.cost) return state
  return {
    permanent: { ...state.permanent, armorTier: next.tier },
    loop: { ...state.loop, money: state.loop.money - next.cost },
  }
}

/**
 * How many Command Points a faction's trust unlocks, on top of whatever
 * Leadership already grants (see getStartingCommandPoints). Trust resets
 * every loop, so this bonus is earned and lost with the rest of LoopState.
 */
const TRUST_COMMAND_POINTS: Record<FactionTrustLevel, number> = {
  unknown: 0,
  recognized: 2 * ARMY_SCALE,
  trusted: 5 * ARMY_SCALE,
  subjected: 10 * ARMY_SCALE,
}

/**
 * A faction whose court has been handed convincing forgeries in the player's
 * own hand will not advance them any further this loop. The letters are
 * refutable — but not before the loop runs out, which is the point of them.
 */
export function isAllianceSabotaged(loop: LoopState, factionId: FactionId): boolean {
  return (
    loop.worldFlags.includes("anti_player_sabotaged_an_alliance") ||
    loop.worldFlags.includes(`anti_player_sabotaged_${factionId}`) ||
    // A realm still holding this loop's forgery against the player will not
    // advance them until the accusation has been faced and disproved at its
    // seat — the same block, arrived at by his slower method.
    isFramedUncleared(loop, factionId)
  )
}

/** Marks this loop's forgery against a realm as disproved, lifting the alliance block. */
export function clearAccusation(state: GameState, factionId: FactionId): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: addWorldFlag(state.loop, accusationClearedFlag(factionId)) }
}

/**
 * Raises a faction's trust to at least `level` (never lowers it — calling
 * this with a level the player already has, or has passed, is a no-op) and
 * grants the Command Points that stage of trust unlocks.
 */
export function raiseFactionTrust(
  state: GameState,
  factionId: FactionId,
  level: FactionTrustLevel,
): GameState {
  if (!state.loop.alive) return state
  if (isAllianceSabotaged(state.loop, factionId)) return state
  const current = getTrust(state.loop, factionId)
  if (TRUST_ORDER.indexOf(level) <= TRUST_ORDER.indexOf(current)) return state
  const commandPointBonus = TRUST_COMMAND_POINTS[level] - TRUST_COMMAND_POINTS[current]
  return {
    // Courting the same realms loop after loop is a pattern, and he reads it.
    permanent: observeAlly(state.permanent, factionId),
    loop: {
      ...setTrust(state.loop, factionId, level),
      commandPoints: state.loop.commandPoints + commandPointBonus,
    },
  }
}

/** Records that a faction has been subjected to the player, per Phase 3's Diplomacy master unlock. */
export function subjectFaction(state: GameState, factionId: FactionId): GameState {
  if (state.loop.subjectedFactions.includes(factionId)) return state
  return {
    ...state,
    loop: {
      ...state.loop,
      subjectedFactions: [...state.loop.subjectedFactions, factionId],
    },
  }
}

/**
 * Grants Command Points outside the trust ladder — for instance when allied
 * realms lend contingents and the authority to lead them. Loop-scoped like
 * every other Command Point.
 */
export function grantCommandPoints(state: GameState, amount: number): GameState {
  if (!state.loop.alive || amount === 0) return state
  return {
    ...state,
    loop: { ...state.loop, commandPoints: state.loop.commandPoints + amount },
  }
}

/** Grants units waiting at a location (e.g. a faction lending soldiers once trusted), for the player to collect later. */
export function grantUnitsAtLocation(
  state: GameState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: addUnitsAtLocation(state.loop, locationId, unitId, count) }
}

export function takeUnitsAtLocation(
  state: GameState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: takeUnits(state.loop, locationId, unitId, count) }
}

export function takeAllUnitsAtLocation(state: GameState, locationId: LocationId): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: takeAllUnits(state.loop, locationId) }
}

export function leaveUnitsAtLocation(
  state: GameState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: leaveUnits(state.loop, locationId, unitId, count) }
}

/** Permanently disbands units from the current army rather than parking them at a location. */
export function releaseUnitsFromArmy(
  state: GameState,
  unitId: UnitId,
  count: number,
): GameState {
  if (!state.loop.alive) return state
  return { ...state, loop: releaseUnits(state.loop, unitId, count) }
}

/**
 * Removes every unit parked at a location. Used when a garrison is given
 * orders in the player's name and marches off to obey them — units left
 * behind are safe from upkeep, but they were never safe from being lied to.
 */
export function destroyGarrisonAt(state: GameState, locationId: LocationId): GameState {
  const { [locationId]: _removed, ...rest } = state.loop.armiesByLocation
  return { ...state, loop: { ...state.loop, armiesByLocation: rest } }
}

/** Replaces the player's current army with whatever survived a battle. */
export function applyBattleCasualties(state: GameState, survivingArmy: ArmyStack[]): GameState {
  return { ...state, loop: { ...state.loop, currentArmy: survivingArmy } }
}

/**
 * Trains an ability level: consumes the level's trainingDays (resolving any
 * events along the way, exactly like any other day-consuming action).
 *
 * If the level has no testBattleId, the level is granted immediately once
 * training completes. If it does (the "final test" step of the training
 * structure — see GAME_CREATION_INSTRUCTIONS.md Phase 3), the days are spent
 * but the level is withheld until the caller resolves that battle and calls
 * completeAbilityTraining with the result.
 */
export function trainAbility(
  state: GameState,
  abilityId: AbilityId,
  level: Exclude<AbilityLevel, "none">,
): GameState {
  if (!state.loop.alive) return state
  const levelDefinition = getAbilityById(abilityId)?.levels.find(
    (l) => l.level === level,
  )
  if (!levelDefinition) {
    throw new Error(`Unknown ability level: ${abilityId} ${level}`)
  }
  if (!canTrain(state.permanent, abilityId, level, state.loop.currentLocationId, state.loop)) {
    throw new Error(`Requirements not met to train ${abilityId} to ${level}`)
  }
  const afterTrainingDays = advanceDay(state, levelDefinition.trainingDays)
  // Where the player goes to learn is one of the few things about them that is
  // stable across loops, which makes it worth his while to watch.
  const observed: GameState = {
    ...afterTrainingDays,
    permanent: observeTrainingSite(
      afterTrainingDays.permanent,
      state.loop.currentLocationId,
    ),
  }
  if (levelDefinition.testBattleId) {
    return observed
  }
  return {
    ...observed,
    permanent: applyAbilityTraining(
      observed.permanent,
      abilityId,
      level,
      state.loop.currentLocationId,
      state.loop,
    ),
  }
}

/** Grants an ability level after its test battle has been won. See trainAbility. */
export function completeAbilityTraining(
  state: GameState,
  abilityId: AbilityId,
  level: Exclude<AbilityLevel, "none">,
): GameState {
  return {
    ...state,
    permanent: applyAbilityTraining(
      state.permanent,
      abilityId,
      level,
      state.loop.currentLocationId,
      state.loop,
    ),
  }
}

/** Permanently records knowledge the player has learned (e.g. from dialogue). */
export function grantKnowledge(
  state: GameState,
  knowledgeIds: string[],
): GameState {
  return { ...state, permanent: addKnowledge(state.permanent, knowledgeIds) }
}

/**
 * Death resets all temporary progress (LoopState) while preserving
 * abilities, knowledge and the other permanent fields.
 */
export function killPlayer(state: GameState): GameState {
  const survivedDays = state.loop.currentDay
  const permanent: PermanentState = {
    ...state.permanent,
    totalDeaths: state.permanent.totalDeaths + 1,
    longestSurvival: Math.max(state.permanent.longestSurvival, survivedDays),
  }
  const diedLoop: LoopState = { ...state.loop, alive: false }
  return {
    permanent,
    loop: diedLoop,
  }
}

/** Begins a new loop after death. The player cannot trigger this manually before dying. */
export function startNewLoop(state: GameState): GameState {
  if (state.loop.alive) {
    throw new Error("Cannot start a new loop while the player is still alive")
  }
  return {
    permanent: state.permanent,
    loop: createNewLoop(state.loop.loopNumber + 1, state.permanent),
  }
}
